import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, BASE, getToken } from '@/lib/api';
import { getVoiceInputCapability } from '@boox/ui-foundation';

type GeminiLiveState = 'idle' | 'connecting' | 'ready' | 'listening' | 'speaking' | 'failed';

interface GeminiLiveSessionResponse {
  sessionId: string;
  provider: 'gemini_live';
  transport: 'websocket';
  model: string;
  websocketUrl: string;
  expiresAt: string;
  inputAudioFormat: 'pcm16';
  outputAudioFormat: 'pcm16';
  supportsBargeIn: true;
  supportsPartialTranscript: true;
  setup: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

function buildAuthenticatedWsUrl(baseUrl: string): string {
  const token = getToken();
  if (!token) return baseUrl;
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('token', token);
  return url.toString();
}

interface UseGeminiLiveSessionOpts {
  role?: string;
  surface: string;
  profile?: string;
  lang?: string;
  onError?: (message: string) => void;
  onInputTranscript?: (text: string) => void;
  onOutputTranscript?: (text: string) => void;
}

interface AudioChunkPart {
  inlineData?: {
    data?: string;
    mimeType?: string;
  };
  text?: string;
}

function decodeBase64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function encodeBytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function floatTo16BitPCM(input: Float32Array): Int16Array {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, input[i] ?? 0));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
}

function downsampleBuffer(buffer: Float32Array, inputSampleRate: number, outputSampleRate: number): Float32Array {
  if (outputSampleRate >= inputSampleRate) return buffer;
  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i += 1) {
      accum += buffer[i] ?? 0;
      count += 1;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}

function pcm16Base64ToFloat32(base64: string): Float32Array {
  const bytes = decodeBase64ToBytes(base64);
  const sampleCount = Math.floor(bytes.byteLength / 2);
  const samples = new Float32Array(sampleCount);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let i = 0; i < sampleCount; i += 1) {
    samples[i] = view.getInt16(i * 2, true) / 0x8000;
  }
  return samples;
}

export function useGeminiLiveSession(opts: UseGeminiLiveSessionOpts) {
  const capability = useMemo(() => getVoiceInputCapability(), []);
  const [session, setSession] = useState<GeminiLiveSessionResponse | null>(null);
  const [state, setState] = useState<GeminiLiveState>('idle');
  const [connected, setConnected] = useState(false);
  const [inputTranscript, setInputTranscript] = useState('');
  const [outputTranscript, setOutputTranscript] = useState('');

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const playbackTimeRef = useRef(0);

  const reportError = useCallback((message: string) => {
    setState('failed');
    opts.onError?.(message);
  }, [opts]);

  const ensureAudioContext = useCallback(async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });
    }
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  const stopPlayback = useCallback(() => {
    const context = audioContextRef.current;
    if (!context) return;
    playbackTimeRef.current = context.currentTime;
  }, []);

  const playAudioChunk = useCallback(async (base64Pcm: string) => {
    const context = await ensureAudioContext();
    const floatSamples = pcm16Base64ToFloat32(base64Pcm);
    const buffer = context.createBuffer(1, floatSamples.length, 24000);
    buffer.copyToChannel(Float32Array.from(floatSamples), 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    const startAt = Math.max(context.currentTime, playbackTimeRef.current);
    source.start(startAt);
    playbackTimeRef.current = startAt + buffer.duration;
  }, [ensureAudioContext]);

  const closeMicrophone = useCallback(() => {
    processorRef.current?.disconnect();
    processorRef.current = null;
    sourceNodeRef.current?.disconnect();
    sourceNodeRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, []);

  const disconnect = useCallback(() => {
    closeMicrophone();
    stopPlayback();
    wsRef.current?.close();
    wsRef.current = null;
    setConnected(false);
    setSession(null);
    setInputTranscript('');
    setOutputTranscript('');
    setState('idle');
  }, [closeMicrophone, stopPlayback]);

  useEffect(() => () => {
    disconnect();
  }, [disconnect]);

  const connect = useCallback(async () => {
    if (!capability.supportsRealtime) {
      throw new Error('Gemini Live requires realtime browser voice support.');
    }
    if (wsRef.current && session) return session;

    setState('connecting');
    const nextSession = await api.post<GeminiLiveSessionResponse>('/voice/live/token', {
      role: opts.role,
      surface: opts.surface,
      profile: opts.profile,
      lang: opts.lang,
      browser: {
        supportsRealtime: capability.supportsRealtime,
        supportsNativeLive: capability.supportsNativeLive,
        supportsUploadFallback: capability.supportsUploadFallback,
        supportsAlwaysOnCommands: capability.supportsAlwaysOnCommands,
        browserFamily: capability.browserFamily,
        platform: capability.platform,
        matrixKey: capability.matrixKey,
        recommendedInteraction: capability.recommendedInteraction,
      },
    });

    await ensureAudioContext();

    await new Promise<void>((resolve, reject) => {
      const wsBase = nextSession.websocketUrl.startsWith('ws')
        ? nextSession.websocketUrl
        : `${BASE.replace(/\/api\/v1$/, '').replace(/^http/, 'ws')}${nextSession.websocketUrl}`;
      const ws = new WebSocket(buildAuthenticatedWsUrl(wsBase));
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ setup: nextSession.setup }));
      };
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as Record<string, unknown>;
          if (message['setupComplete']) {
            setConnected(true);
            setSession(nextSession);
            setState('ready');
            resolve();
            return;
          }

          const serverContent = message['serverContent'] as Record<string, unknown> | undefined;
          if (!serverContent) return;

          if (serverContent['inputTranscription']) {
            const text = String((serverContent['inputTranscription'] as Record<string, unknown>)['text'] ?? '').trim();
            if (text) {
              setInputTranscript(text);
              opts.onInputTranscript?.(text);
            }
          }

          if (serverContent['outputTranscription']) {
            const text = String((serverContent['outputTranscription'] as Record<string, unknown>)['text'] ?? '').trim();
            if (text) {
              setOutputTranscript(text);
              opts.onOutputTranscript?.(text);
            }
          }

          if (serverContent['interrupted'] === true) {
            stopPlayback();
            setState('ready');
          }

          const modelTurn = serverContent['modelTurn'] as Record<string, unknown> | undefined;
          const parts = Array.isArray(modelTurn?.['parts']) ? modelTurn?.['parts'] as AudioChunkPart[] : [];
          if (parts.length > 0) {
            setState('speaking');
          }
          for (const part of parts) {
            const mimeType = part.inlineData?.mimeType ?? '';
            const data = part.inlineData?.data ?? '';
            if (mimeType.startsWith('audio/pcm') && data) {
              void playAudioChunk(data);
            }
          }

          if (serverContent['turnComplete'] === true) {
            setState('ready');
          }
        } catch (error) {
          reject(error);
        }
      };
      ws.onerror = () => {
        reject(new Error('Gemini Live websocket error'));
      };
      ws.onclose = () => {
        wsRef.current = null;
        setConnected(false);
        setState((prev) => (prev === 'idle' ? prev : 'failed'));
      };
    }).catch((error) => {
      wsRef.current?.close();
      wsRef.current = null;
      const message = error instanceof Error ? error.message : 'Failed to connect to Gemini Live';
      reportError(message);
      throw error;
    });

    return nextSession;
  }, [capability.browserFamily, capability.matrixKey, capability.platform, capability.recommendedInteraction, capability.supportsAlwaysOnCommands, capability.supportsNativeLive, capability.supportsRealtime, capability.supportsUploadFallback, ensureAudioContext, opts.lang, opts.onInputTranscript, opts.onOutputTranscript, opts.profile, opts.role, opts.surface, playAudioChunk, reportError, session, stopPlayback]);

  const startUserTurn = useCallback(async () => {
    if (!capability.supportsRealtime) {
      reportError('Gemini Live requires realtime browser voice support.');
      return;
    }
    if (!wsRef.current || !session) {
      await connect();
    }
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      reportError('Gemini Live websocket is not ready.');
      return;
    }

    setInputTranscript('');
    setOutputTranscript('');
    stopPlayback();

    const context = await ensureAudioContext();
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    mediaStreamRef.current = stream;
    const source = context.createMediaStreamSource(stream);
    sourceNodeRef.current = source;
    const processor = context.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    processor.onaudioprocess = (event) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const inputData = event.inputBuffer.getChannelData(0);
      const downsampled = downsampleBuffer(inputData, context.sampleRate, 16000);
      const pcm = floatTo16BitPCM(downsampled);
      const bytes = new Uint8Array(pcm.buffer);
      wsRef.current.send(JSON.stringify({
        realtimeInput: {
          audio: {
            data: encodeBytesToBase64(bytes),
            mimeType: 'audio/pcm;rate=16000',
          },
        },
      }));
    };

    source.connect(processor);
    processor.connect(context.destination);
    setState('listening');
  }, [capability.supportsRealtime, connect, ensureAudioContext, reportError, session, stopPlayback]);

  const stopUserTurn = useCallback(() => {
    closeMicrophone();
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        realtimeInput: {
          audioStreamEnd: true,
        },
      }));
    }
    setState('ready');
  }, [closeMicrophone]);

  const interruptAssistant = useCallback(() => {
    stopPlayback();
    setState('ready');
  }, [stopPlayback]);

  return {
    capability,
    connected,
    session,
    state,
    inputTranscript,
    outputTranscript,
    connect,
    disconnect,
    startUserTurn,
    stopUserTurn,
    interruptAssistant,
  };
}
